import { Fragment } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StepDef {
  id: string;
  label: string;
  /** Resumo do que já foi preenchido — aparece sob o nome do passo. */
  summary: string;
  done: boolean;
}

/**
 * Trilho clássico círculo — traço — círculo. Concluído mostra check, o atual
 * fica em destaque e o traço até o próximo preenche quando o passo fecha.
 * Dá pra clicar em qualquer passo: kit, frete e premissas dependem um do
 * outro, então travar a ordem só atrapalharia.
 */
export function Stepper({
  steps,
  current,
  onSelect,
}: {
  steps: StepDef[];
  current: string;
  onSelect: (id: string) => void;
}) {
  return (
    <nav aria-label="Passos do orçamento" className="overflow-x-auto px-1 pb-1 pt-1.5">
      <ol className="flex min-w-[640px] items-start">
        {steps.map((step, i) => {
          const active = step.id === current;
          const last = i === steps.length - 1;
          return (
            <Fragment key={step.id}>
              <li className="w-28 shrink-0">
                <button
                  type="button"
                  onClick={() => onSelect(step.id)}
                  aria-current={active ? 'step' : undefined}
                  className="group flex w-full flex-col items-center gap-2 text-center focus:outline-none"
                >
                  <span
                    className={cn(
                      'flex size-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all duration-300 group-focus-visible:ring-2 group-focus-visible:ring-primary/40',
                      active
                        ? 'scale-110 border-primary bg-primary text-bg-main shadow-[0_0_0_4px_rgb(var(--primary)/0.15)]'
                        : step.done
                          ? 'border-primary bg-primary/15 text-primary'
                          : 'border-border-strong bg-card text-text-secondary group-hover:border-primary'
                    )}
                  >
                    {step.done && !active ? (
                      <Check size={16} strokeWidth={3} className="animate-pop" />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className="flex flex-col">
                    <span
                      className={cn(
                        'text-sm font-medium',
                        active ? 'text-text-title' : 'text-text-body'
                      )}
                    >
                      {step.label}
                    </span>
                    <span className="max-w-28 truncate text-xs text-text-secondary">
                      {step.summary}
                    </span>
                  </span>
                </button>
              </li>
              {!last && (
                <li aria-hidden className="mt-[17px] flex-1 px-1">
                  <span className="block h-0.5 w-full overflow-hidden rounded-full bg-border">
                    <span
                      className={cn(
                        'block h-full origin-left bg-primary transition-transform duration-500 ease-out',
                        step.done ? 'scale-x-100' : 'scale-x-0'
                      )}
                    />
                  </span>
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
